import { useEffect, useMemo, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdSave } from 'react-icons/md';
import { clearIndustriesCache, Industry, MarketplaceDisplay, productKey } from 'services/industryService';

// Same list as DEMAND_COUNTRIES on the back (services/demandCountries.ts)
const COUNTRIES = [
    { code: 'FR', label: '🇫🇷 France' },
    { code: 'BE', label: '🇧🇪 Belgique' },
    { code: 'CH', label: '🇨🇭 Suisse' },
    { code: 'ES', label: '🇪🇸 Espagne' },
];

interface Row {
    hidden: boolean;
    countries: string[];
    displayPrice: string;
    // Per country; '' = follow the open demand
    volumes: Record<string, string>;
}

const rowFrom = (m?: MarketplaceDisplay): Row => ({
    hidden: !!m?.hidden,
    countries: m?.countries ?? [],
    displayPrice: m?.displayPrice != null ? String(m.displayPrice) : '',
    volumes: Object.fromEntries(Object.entries(m?.volumes ?? {}).map(([cc, v]) => [cc, String(v)])),
});

const volumesKey = (v: Record<string, string>) =>
    Object.entries(v)
        .filter(([, value]) => value !== '')
        .sort()
        .join();

const sameRow = (a: Row, b: Row) =>
    a.hidden === b.hidden &&
    a.displayPrice === b.displayPrice &&
    a.countries.join() === b.countries.join() &&
    volumesKey(a.volumes) === volumesKey(b.volumes);

// Open demand per product and country, from GET /purchase-order/volumes
type DemandByProduct = Record<string, Record<string, number>>;

const rowKey = (industryId: string, subName: string) => `${industryId}|||${subName}`;

/**
 * Back-office: how each product (sub-industry) shows on the seller marketplace —
 * shown or hidden, which country cards, and the "à partir de" price.
 */
export default function BackOfficeMarketplaceDisplay() {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [saved, setSaved] = useState<Record<string, Row>>({});
    const [rows, setRows] = useState<Record<string, Row>>({});
    const [saving, setSaving] = useState<string | null>(null);
    const [demand, setDemand] = useState<DemandByProduct>({});

    useEffect(() => {
        axios
            .get(`${CONFIG.apiUrl}/industry`)
            .then((res) => {
                const data: Industry[] = res.data || [];
                const initial: Record<string, Row> = {};
                for (const ind of data) {
                    for (const sub of ind.subIndustries) initial[rowKey(ind._id, sub.name)] = rowFrom(sub.marketplace);
                }
                setIndustries(data);
                setSaved(initial);
                setRows(initial);
            })
            .catch((e) => console.error('Error fetching industries:', e))
            .finally(() => setLoading(false));

        axios
            .get(`${CONFIG.apiUrl}/purchase-order/volumes`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            })
            .then((res) => {
                const byProduct: DemandByProduct = {};
                for (const vol of res.data || []) {
                    const countries = vol.countries?.length ? vol.countries : [{ country: 'FR', leads: vol.totalLeads }];
                    byProduct[productKey(vol.subIndustry)] = Object.fromEntries(
                        countries.map((c: any) => [c.country, c.leads ?? 0])
                    );
                }
                setDemand(byProduct);
            })
            .catch((e) => console.error('Error fetching purchase order volumes:', e));
    }, []);

    // Countries that get a card on the seller marketplace — same rule as the seller page
    const effectiveCountries = (row: Row, subName: string) => {
        if (row.countries.length) return row.countries;
        const fromDemand = Object.keys(demand[productKey(subName)] ?? {});
        return fromDemand.length ? COUNTRIES.map((c) => c.code).filter((c) => fromDemand.includes(c)) : ['FR'];
    };

    const patchRow = (key: string, patch: Partial<Row>) => setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const toggleCountry = (key: string, code: string) => {
        const current = rows[key].countries;
        const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
        // Keep the reference order so "dirty" detection doesn't depend on click order
        patchRow(key, { countries: COUNTRIES.map((c) => c.code).filter((c) => next.includes(c)) });
    };

    const saveRow = async (industry: Industry, subName: string) => {
        const key = rowKey(industry._id, subName);
        const row = rows[key];
        const shown = effectiveCountries(row, subName);
        // Only volumes for countries that actually get a card
        const volumes = Object.fromEntries(Object.entries(row.volumes).filter(([cc, v]) => v !== '' && shown.includes(cc)));
        setSaving(key);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(
                `${CONFIG.apiUrl}/industry/${industry._id}/marketplace`,
                { subIndustry: subName, hidden: row.hidden, countries: row.countries, displayPrice: row.displayPrice, volumes },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const stored = rowFrom(res.data.marketplace);
            setSaved((prev) => ({ ...prev, [key]: stored }));
            setRows((prev) => ({ ...prev, [key]: stored }));
            // The seller marketplace reads industries through the cached service
            clearIndustriesCache();
        } catch (error: any) {
            alert(error.response?.data?.message || "Échec de l'enregistrement");
        } finally {
            setSaving(null);
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return industries;
        return industries
            .map((ind) =>
                ind.name.toLowerCase().includes(q)
                    ? ind
                    : { ...ind, subIndustries: ind.subIndustries.filter((s) => s.name.toLowerCase().includes(q)) }
            )
            .filter((ind) => ind.subIndustries.length > 0);
    }, [industries, search]);

    const inputClass =
        'w-24 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white';

    return (
        <div className="mt-3 flex flex-col gap-5">
            <Card extra="p-6">
                <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Marketplace vendeur</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Pour chaque type de produit : affiché ou non sur la marketplace vendeur, les pays affichés (une carte par
                    pays), le volume de leads par pays et le prix « à partir de » côté vendeur. Sans pays, ce sont ceux de la
                    demande ouverte ; un volume vide suit la demande (valeur grisée) ; sans prix, celui des ordres
                    d&apos;achat puis du catalogue.
                </p>
                <div className="relative mt-4 max-w-md">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MdSearch className="text-gray-400" />
                    </span>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un secteur ou un produit"
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                </div>
            </Card>

            {loading ? (
                <p className="py-10 text-center text-gray-500">Chargement…</p>
            ) : filtered.length === 0 ? (
                <p className="py-10 text-center text-gray-500">Aucun produit.</p>
            ) : (
                filtered.map((industry) => (
                    <Card key={industry._id} extra="p-6">
                        <h3 className="mb-3 text-lg font-bold text-navy-700 dark:text-white">{industry.name}</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-navy-600">
                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Produit</th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Affiché</th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Pays</th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Volume</th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">À partir de</th>
                                        <th className="pb-3" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {industry.subIndustries.map((sub) => {
                                        const key = rowKey(industry._id, sub.name);
                                        const row = rows[key];
                                        if (!row) return null;
                                        const dirty = !sameRow(row, saved[key]);
                                        return (
                                            <tr key={key} className="border-b border-gray-100 last:border-0 dark:border-navy-700">
                                                <td className={`py-3 pr-4 font-medium ${row.hidden ? 'text-gray-400' : 'text-navy-700 dark:text-white'}`}>
                                                    {sub.name}
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <button
                                                        onClick={() => patchRow(key, { hidden: !row.hidden })}
                                                        title={row.hidden ? 'Masqué' : 'Affiché'}
                                                        className={`flex h-6 w-[42px] rounded-full p-[3px] transition-colors ${
                                                            row.hidden ? 'justify-start bg-gray-300' : 'justify-end bg-green-500'
                                                        }`}
                                                    >
                                                        <span className="block h-[18px] w-[18px] rounded-full bg-white shadow" />
                                                    </button>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {COUNTRIES.map((c) => {
                                                            const on = row.countries.includes(c.code);
                                                            return (
                                                                <button
                                                                    key={c.code}
                                                                    onClick={() => toggleCountry(key, c.code)}
                                                                    className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                                                        on
                                                                            ? 'border-brand-500 bg-brand-500 text-white'
                                                                            : 'border-gray-300 bg-white text-gray-600 hover:border-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300'
                                                                    }`}
                                                                >
                                                                    {c.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    {row.countries.length === 0 && (
                                                        <p className="mt-1 text-xs text-gray-400">Selon la demande ouverte</p>
                                                    )}
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <div className="flex flex-col gap-1">
                                                        {effectiveCountries(row, sub.name).map((cc) => {
                                                            const known = demand[productKey(sub.name)]?.[cc] ?? 0;
                                                            const value = row.volumes[cc] ?? '';
                                                            return (
                                                                <div key={cc} className="flex items-center gap-1.5">
                                                                    <span className="w-6 text-xs font-semibold text-gray-500">{cc}</span>
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        step="1"
                                                                        value={value}
                                                                        placeholder={String(known)}
                                                                        title={`Demande connue : ${known} leads`}
                                                                        onChange={(e) =>
                                                                            patchRow(key, { volumes: { ...row.volumes, [cc]: e.target.value } })
                                                                        }
                                                                        className={inputClass}
                                                                    />
                                                                    {value !== '' && (
                                                                        <button
                                                                            onClick={() => patchRow(key, { volumes: { ...row.volumes, [cc]: '' } })}
                                                                            title={`Revenir à la demande (${known})`}
                                                                            className="text-xs text-brand-500 hover:underline"
                                                                        >
                                                                            auto
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <div className="flex items-center gap-1.5">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            value={row.displayPrice}
                                                            placeholder="Auto"
                                                            onChange={(e) => patchRow(key, { displayPrice: e.target.value })}
                                                            className={inputClass}
                                                        />
                                                        <span className="text-gray-500">€</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-right">
                                                    <button
                                                        onClick={() => saveRow(industry, sub.name)}
                                                        disabled={!dirty || saving === key}
                                                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
                                                    >
                                                        <MdSave className="h-4 w-4" />
                                                        {saving === key ? 'Enregistrement…' : 'Enregistrer'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                ))
            )}
        </div>
    );
}
